// src/services/activity.js
import { db } from './firebase';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  getDocs,
  Timestamp,
  limit
} from 'firebase/firestore';

// Types d'activités
export const ACTIVITY_TYPES = {
  // ===== RÉSERVATIONS =====
  RESERVATION_CREATED: 'reservation_created',
  RESERVATION_MODIFIED: 'reservation_modified',
  RESERVATION_CANCELLED: 'reservation_cancelled',
  RESERVATION_CONFIRMED: 'reservation_confirmed',
  RESERVATION_RECOVERED: 'reservation_recovered',
  
  // ===== CGR =====
  CGR_ACCEPTED: 'cgr_accepted',
  
  // ===== CHAMBRES =====
  ROOM_CHANGED: 'room_changed',
  ROOM_ADDED: 'room_added',
  ROOM_REMOVED: 'room_removed',
  OCCUPANCY_CHANGED: 'occupancy_changed',
  
  // ===== DATES =====
  DATES_CHANGED: 'dates_changed',
  
  // ===== PRIX =====
  PRICE_ADJUSTED: 'price_adjusted',
  
  // ===== CLIENT =====
  CLIENT_INFO_UPDATED: 'client_info_updated',
  GUEST_COUNT_CHANGED: 'guest_count_changed',
  
  // ===== TRAINS =====
  TRAIN_ARRIVAL_SET: 'train_arrival_set',
  TRAIN_ARRIVAL_CHANGED: 'train_arrival_changed',
  TRAIN_ARRIVAL_REMOVED: 'train_arrival_removed',
  TRAIN_DEPARTURE_SET: 'train_departure_set',
  TRAIN_DEPARTURE_CHANGED: 'train_departure_changed',
  TRAIN_DEPARTURE_REMOVED: 'train_departure_removed',
  
  // ===== RESTAURANT (La Table) =====
  RESTAURANT_BOOKING_ADDED: 'restaurant_booking_added',
  RESTAURANT_BOOKING_MODIFIED: 'restaurant_booking_modified',
  RESTAURANT_BOOKING_CANCELLED: 'restaurant_booking_cancelled',
  
  // ===== CAFÉ (Le Café) =====
  CAFE_BOOKING_ADDED: 'cafe_booking_added',
  CAFE_BOOKING_MODIFIED: 'cafe_booking_modified',
  CAFE_BOOKING_CANCELLED: 'cafe_booking_cancelled',
  
  // ===== SERVICES =====
  SERVICE_ADDED: 'service_added',
  SERVICE_MODIFIED: 'service_modified',
  SERVICE_REMOVED: 'service_removed',
  
  // ===== CONSOMMATIONS =====
  CONSUMPTION_ADDED: 'consumption_added',
  CONSUMPTION_MODIFIED: 'consumption_modified', 
  CONSUMPTION_DELETED: 'consumption_deleted',
  
  // ===== PAIEMENTS =====
  PAYMENT_RECEIVED: 'payment_received',
  PAYMENT_REFUNDED: 'payment_refunded',
  DEPOSIT_REQUESTED: 'deposit_requested',
  CARD_ADDED: 'card_added',
  CARD_REMOVED: 'card_removed',
  CARD_CHARGED: 'card_charged',
  PAYMENT_LINK_SENT: 'payment_link_sent',
  
  // ===== COMMUNICATIONS =====
  EMAIL_SENT: 'email_sent',
  SMS_SENT: 'sms_sent',
  NOTE_ADDED: 'note_added',
  
  // ===== CHECK-IN/OUT =====
  CHECKED_IN: 'checked_in',
  CHECKED_OUT: 'checked_out',
  NO_SHOW: 'no_show',
  
  // ===== DOCUMENTS & FACTURES =====
  INVOICE_CREATED: 'invoice_created',
  INVOICE_CANCELLED: 'invoice_cancelled',
  DOCUMENT_UPLOADED: 'document_uploaded',
  
  // ===== COUPONS =====
  COUPON_APPLIED: 'coupon_applied',
  COUPON_REMOVED: 'coupon_removed'
};

// ============================================================
// FONCTION PRINCIPALE
// ============================================================

/**
 * Enregistrer une activité
 * @param {Object} params - Paramètres de l'activité
 * @param {string} params.type - Type d'activité (voir ACTIVITY_TYPES)
 * @param {string} params.reservationId - ID de la réservation concernée
 * @param {string} params.userId - ID de l'utilisateur qui fait l'action
 * @param {string} params.userName - Nom de l'utilisateur
 * @param {string} params.description - Description de l'activité
 * @param {Object} params.metadata - Données supplémentaires (avant/après pour les modifications)
 */
export async function logActivity({
  type,
  reservationId,
  userId,
  userName,
  description,
  metadata = {}
}) {
  try {
    const activity = {
      type,
      reservationId,
      userId,
      userName,
      description,
      metadata,
      timestamp: Timestamp.now()
    };

    await addDoc(collection(db, 'activity'), activity);
    console.log('✅ Activité enregistrée:', description);
    return true;
  } catch (error) {
    console.error('❌ Erreur lors de l\'enregistrement de l\'activité:', error);
    return false;
  }
}

// ============================================================
// FONCTIONS DE RÉCUPÉRATION
// ============================================================

/**
 * Récupérer l'historique d'une réservation
 * @param {string} reservationId - ID de la réservation
 * @returns {Array} Liste des activités triées par date
 */
export async function getReservationHistory(reservationId) {
  try {
    const q = query(
      collection(db, 'activity'),
      where('reservationId', '==', reservationId),
      orderBy('timestamp', 'desc')
    );

    const snapshot = await getDocs(q);
    const activities = [];
    
    snapshot.forEach((doc) => {
      activities.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return activities;
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'historique:', error);
    return [];
  }
}

/**
 * Récupérer les dernières activités globales
 * @param {number} limitCount - Nombre d'activités à récupérer
 * @returns {Array} Liste des dernières activités
 */
export async function getRecentActivities(limitCount = 50) {
  try {
    const q = query(
      collection(db, 'activity'),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    const activities = [];
    
    snapshot.forEach((doc) => {
      activities.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return activities;
  } catch (error) {
    console.error('Erreur lors de la récupération des activités récentes:', error);
    return [];
  }
}

// ============================================================
// HELPERS - RÉSERVATION
// ============================================================

/**
 * Log création de réservation
 */
export async function logReservationCreated(reservationId, userId, userName, { source, totalAmount, roomCount }) {
  return logActivity({
    type: ACTIVITY_TYPES.RESERVATION_CREATED,
    reservationId,
    userId,
    userName,
    description: `Réservation créée via ${source === 'website' ? 'site web' : 'admin'} - ${roomCount} chambre(s) - ${totalAmount}€`,
    metadata: { source, totalAmount, roomCount }
  });
}

/**
 * Log récupération de réservation (après annulation)
 */
export async function logReservationRecovered(reservationId, userId, userName, { oldRoom, newRoom }) {
  return logActivity({
    type: ACTIVITY_TYPES.RESERVATION_RECOVERED,
    reservationId,
    userId,
    userName,
    description: `Réservation récupérée${newRoom !== oldRoom ? ` - Chambre ${oldRoom} → ${newRoom}` : ''}`,
    metadata: { oldRoom, newRoom }
  });
}

// ============================================================
// HELPERS - CGR
// ============================================================

/**
 * Log acceptation des CGR
 */
export async function logCGRAccepted(reservationId, { ipAddress, userAgent, cgvUrl }) {
  return logActivity({
    type: ACTIVITY_TYPES.CGR_ACCEPTED,
    reservationId,
    userId: 'client',
    userName: 'Client',
    description: `CGR acceptées${ipAddress ? ` (IP: ${ipAddress})` : ''}`,
    metadata: { ipAddress, userAgent, cgvUrl, acceptedAt: new Date().toISOString() }
  });
}

// ============================================================
// HELPERS - CHAMBRES
// ============================================================

/**
 * Log changement de chambre
 */
export async function logRoomChange(reservationId, userId, userName, oldRoom, newRoom) {
  return logActivity({
    type: ACTIVITY_TYPES.ROOM_CHANGED,
    reservationId,
    userId,
    userName,
    description: `Chambre modifiée: ${oldRoom} → ${newRoom}`,
    metadata: { oldRoom, newRoom }
  });
}

/**
 * Log ajout de chambre
 */
export async function logRoomAdded(reservationId, userId, userName, { roomNumber, roomType, pricePerNight }) {
  return logActivity({
    type: ACTIVITY_TYPES.ROOM_ADDED,
    reservationId,
    userId,
    userName,
    description: `Chambre ${roomNumber} ajoutée (${roomType}) - ${pricePerNight}€/nuit`,
    metadata: { roomNumber, roomType, pricePerNight }
  });
}

/**
 * Log suppression de chambre
 */
export async function logRoomRemoved(reservationId, userId, userName, { roomNumber, roomType }) {
  return logActivity({
    type: ACTIVITY_TYPES.ROOM_REMOVED,
    reservationId,
    userId,
    userName,
    description: `Chambre ${roomNumber} retirée (${roomType})`,
    metadata: { roomNumber, roomType }
  });
}

/**
 * Log changement d'occupation
 */
export async function logOccupancyChanged(reservationId, userId, userName, { roomNumber, oldOccupancy, newOccupancy }) {
  return logActivity({
    type: ACTIVITY_TYPES.OCCUPANCY_CHANGED,
    reservationId,
    userId,
    userName,
    description: `Occupation chambre ${roomNumber}: ${oldOccupancy} → ${newOccupancy} personne(s)`,
    metadata: { roomNumber, oldOccupancy, newOccupancy }
  });
}

// ============================================================
// HELPERS - DATES
// ============================================================

/**
 * Log changement de dates
 */
export async function logDatesChange(reservationId, userId, userName, oldDates, newDates) {
  return logActivity({
    type: ACTIVITY_TYPES.DATES_CHANGED,
    reservationId,
    userId,
    userName,
    description: `Dates modifiées: ${oldDates.checkIn} - ${oldDates.checkOut} → ${newDates.checkIn} - ${newDates.checkOut}`,
    metadata: {
      oldCheckIn: oldDates.checkIn,
      oldCheckOut: oldDates.checkOut,
      newCheckIn: newDates.checkIn,
      newCheckOut: newDates.checkOut
    }
  });
}

// ============================================================
// HELPERS - PRIX
// ============================================================

/**
 * Log modification de prix
 */
export async function logPriceChange(reservationId, userId, userName, oldPrice, newPrice, reason) {
  return logActivity({
    type: ACTIVITY_TYPES.PRICE_ADJUSTED,
    reservationId,
    userId,
    userName,
    description: `Prix modifié de €${oldPrice} à €${newPrice}${reason ? ` - ${reason}` : ''}`,
    metadata: {
      oldPrice,
      newPrice,
      reason,
      difference: newPrice - oldPrice
    }
  });
}

// ============================================================
// HELPERS - CLIENT
// ============================================================

/**
 * Log modification infos client
 */
export async function logClientInfoUpdated(reservationId, userId, userName, { changedFields }) {
  return logActivity({
    type: ACTIVITY_TYPES.CLIENT_INFO_UPDATED,
    reservationId,
    userId,
    userName,
    description: `Informations client modifiées: ${changedFields.join(', ')}`,
    metadata: { changedFields }
  });
}

// ============================================================
// HELPERS - TRAINS
// ============================================================

/**
 * Log train arrivée défini
 */
export async function logTrainArrivalSet(reservationId, userId, userName, { time }) {
  return logActivity({
    type: ACTIVITY_TYPES.TRAIN_ARRIVAL_SET,
    reservationId,
    userId,
    userName,
    description: `Train d'arrivée défini: ${time}`,
    metadata: { time }
  });
}

/**
 * Log train arrivée modifié
 */
export async function logTrainArrivalChanged(reservationId, userId, userName, { oldTime, newTime }) {
  return logActivity({
    type: ACTIVITY_TYPES.TRAIN_ARRIVAL_CHANGED,
    reservationId,
    userId,
    userName,
    description: `Train d'arrivée modifié: ${oldTime} → ${newTime}`,
    metadata: { oldTime, newTime }
  });
}

/**
 * Log train arrivée supprimé
 */
export async function logTrainArrivalRemoved(reservationId, userId, userName) {
  return logActivity({
    type: ACTIVITY_TYPES.TRAIN_ARRIVAL_REMOVED,
    reservationId,
    userId,
    userName,
    description: `Train d'arrivée supprimé`,
    metadata: {}
  });
}

/**
 * Log train départ défini
 */
export async function logTrainDepartureSet(reservationId, userId, userName, { time }) {
  return logActivity({
    type: ACTIVITY_TYPES.TRAIN_DEPARTURE_SET,
    reservationId,
    userId,
    userName,
    description: `Train de départ défini: ${time}`,
    metadata: { time }
  });
}

/**
 * Log train départ modifié
 */
export async function logTrainDepartureChanged(reservationId, userId, userName, { oldTime, newTime }) {
  return logActivity({
    type: ACTIVITY_TYPES.TRAIN_DEPARTURE_CHANGED,
    reservationId,
    userId,
    userName,
    description: `Train de départ modifié: ${oldTime} → ${newTime}`,
    metadata: { oldTime, newTime }
  });
}

/**
 * Log train départ supprimé
 */
export async function logTrainDepartureRemoved(reservationId, userId, userName) {
  return logActivity({
    type: ACTIVITY_TYPES.TRAIN_DEPARTURE_REMOVED,
    reservationId,
    userId,
    userName,
    description: `Train de départ supprimé`,
    metadata: {}
  });
}

// ============================================================
// HELPERS - RESTAURANT (La Table)
// ============================================================

/**
 * Log réservation restaurant ajoutée
 */
export async function logRestaurantBookingAdded(reservationId, userId, userName, { date, time, guests, tableName }) {
  return logActivity({
    type: ACTIVITY_TYPES.RESTAURANT_BOOKING_ADDED,
    reservationId,
    userId,
    userName,
    description: `Réservation restaurant ajoutée: ${date} à ${time} - ${guests} couverts${tableName ? ` (${tableName})` : ''}`,
    metadata: { date, time, guests, tableName }
  });
}

/**
 * Log réservation restaurant modifiée
 */
export async function logRestaurantBookingModified(reservationId, userId, userName, { date, changes }) {
  return logActivity({
    type: ACTIVITY_TYPES.RESTAURANT_BOOKING_MODIFIED,
    reservationId,
    userId,
    userName,
    description: `Réservation restaurant modifiée (${date}): ${changes}`,
    metadata: { date, changes }
  });
}

/**
 * Log réservation restaurant annulée
 */
export async function logRestaurantBookingCancelled(reservationId, userId, userName, { date, time }) {
  return logActivity({
    type: ACTIVITY_TYPES.RESTAURANT_BOOKING_CANCELLED,
    reservationId,
    userId,
    userName,
    description: `Réservation restaurant annulée: ${date} à ${time}`,
    metadata: { date, time }
  });
}

// ============================================================
// HELPERS - CAFÉ (Le Café)
// ============================================================

/**
 * Log réservation café ajoutée
 */
export async function logCafeBookingAdded(reservationId, userId, userName, { date, time, guests }) {
  return logActivity({
    type: ACTIVITY_TYPES.CAFE_BOOKING_ADDED,
    reservationId,
    userId,
    userName,
    description: `Réservation café ajoutée: ${date} à ${time} - ${guests} personne(s)`,
    metadata: { date, time, guests }
  });
}

/**
 * Log réservation café modifiée
 */
export async function logCafeBookingModified(reservationId, userId, userName, { date, changes }) {
  return logActivity({
    type: ACTIVITY_TYPES.CAFE_BOOKING_MODIFIED,
    reservationId,
    userId,
    userName,
    description: `Réservation café modifiée (${date}): ${changes}`,
    metadata: { date, changes }
  });
}

/**
 * Log réservation café annulée
 */
export async function logCafeBookingCancelled(reservationId, userId, userName, { date, time }) {
  return logActivity({
    type: ACTIVITY_TYPES.CAFE_BOOKING_CANCELLED,
    reservationId,
    userId,
    userName,
    description: `Réservation café annulée: ${date} à ${time}`,
    metadata: { date, time }
  });
}

// ============================================================
// HELPERS - SERVICES
// ============================================================

/**
 * Log service ajouté
 */
export async function logServiceAdded(reservationId, userId, userName, { serviceName, quantity, totalPrice }) {
  return logActivity({
    type: ACTIVITY_TYPES.SERVICE_ADDED,
    reservationId,
    userId,
    userName,
    description: `Service ajouté: ${serviceName}${quantity > 1 ? ` x${quantity}` : ''} - ${totalPrice}€`,
    metadata: { serviceName, quantity, totalPrice }
  });
}

/**
 * Log service modifié
 */
export async function logServiceModified(reservationId, userId, userName, { serviceName, changes }) {
  return logActivity({
    type: ACTIVITY_TYPES.SERVICE_MODIFIED,
    reservationId,
    userId,
    userName,
    description: `Service modifié: ${serviceName} - ${changes}`,
    metadata: { serviceName, changes }
  });
}

/**
 * Log service supprimé
 */
export async function logServiceRemoved(reservationId, userId, userName, { serviceName }) {
  return logActivity({
    type: ACTIVITY_TYPES.SERVICE_REMOVED,
    reservationId,
    userId,
    userName,
    description: `Service supprimé: ${serviceName}`,
    metadata: { serviceName }
  });
}

// ============================================================
// HELPERS - PAIEMENTS
// ============================================================

/**
 * Log paiement reçu
 */
export async function logPayment(reservationId, userId, userName, amount, method, reference) {
  return logActivity({
    type: ACTIVITY_TYPES.PAYMENT_RECEIVED,
    reservationId,
    userId,
    userName,
    description: `Paiement reçu: €${amount} par ${method}`,
    metadata: {
      amount,
      method,
      reference,
      receivedAt: new Date().toISOString()
    }
  });
}

/**
 * Log carte ajoutée
 */
export async function logCardAdded(reservationId, userId, userName, { last4, brand }) {
  return logActivity({
    type: ACTIVITY_TYPES.CARD_ADDED,
    reservationId,
    userId,
    userName,
    description: `Carte bancaire ajoutée: ${brand} **** ${last4}`,
    metadata: { last4, brand }
  });
}

/**
 * Log carte supprimée
 */
export async function logCardRemoved(reservationId, userId, userName, { last4, brand }) {
  return logActivity({
    type: ACTIVITY_TYPES.CARD_REMOVED,
    reservationId,
    userId,
    userName,
    description: `Carte bancaire supprimée: ${brand} **** ${last4}`,
    metadata: { last4, brand }
  });
}

/**
 * Log carte débitée
 */
export async function logCardCharged(reservationId, userId, userName, { amount, last4, brand }) {
  return logActivity({
    type: ACTIVITY_TYPES.CARD_CHARGED,
    reservationId,
    userId,
    userName,
    description: `Carte débitée: €${amount} sur ${brand} **** ${last4}`,
    metadata: { amount, last4, brand }
  });
}

/**
 * Log lien de paiement envoyé
 */
export async function logPaymentLinkSent(reservationId, userId, userName, { amount, recipient }) {
  return logActivity({
    type: ACTIVITY_TYPES.PAYMENT_LINK_SENT,
    reservationId,
    userId,
    userName,
    description: `Lien de paiement envoyé: €${amount} à ${recipient}`,
    metadata: { amount, recipient }
  });
}

// ============================================================
// HELPERS - COMMUNICATIONS
// ============================================================

/**
 * Log email envoyé
 */
export async function logEmailSent(reservationId, userId, userName, emailType, recipient) {
  return logActivity({
    type: ACTIVITY_TYPES.EMAIL_SENT,
    reservationId,
    userId,
    userName,
    description: `Email "${emailType}" envoyé à ${recipient}`,
    metadata: {
      emailType,
      recipient,
      sentAt: new Date().toISOString()
    }
  });
}

/**
 * Log note ajoutée
 */
export async function logNote(reservationId, userId, userName, note) {
  return logActivity({
    type: ACTIVITY_TYPES.NOTE_ADDED,
    reservationId,
    userId,
    userName,
    description: `Note ajoutée: "${note.substring(0, 100)}${note.length > 100 ? '...' : ''}"`,
    metadata: {
      note,
      addedAt: new Date().toISOString()
    }
  });
}

// ============================================================
// HELPERS - DOCUMENTS & FACTURES
// ============================================================

/**
 * Log facture créée
 */
export async function logInvoiceCreated(reservationId, userId, userName, { invoiceNumber, amount }) {
  return logActivity({
    type: ACTIVITY_TYPES.INVOICE_CREATED,
    reservationId,
    userId,
    userName,
    description: `Facture créée: ${invoiceNumber} - ${amount}€`,
    metadata: { invoiceNumber, amount }
  });
}

/**
 * Log facture annulée
 */
export async function logInvoiceCancelled(reservationId, userId, userName, { invoiceNumber }) {
  return logActivity({
    type: ACTIVITY_TYPES.INVOICE_CANCELLED,
    reservationId,
    userId,
    userName,
    description: `Facture annulée: ${invoiceNumber}`,
    metadata: { invoiceNumber }
  });
}

// ============================================================
// HELPERS - COUPONS
// ============================================================

/**
 * Log coupon appliqué
 */
export async function logCouponApplied(reservationId, userId, userName, { code, discount }) {
  return logActivity({
    type: ACTIVITY_TYPES.COUPON_APPLIED,
    reservationId,
    userId,
    userName,
    description: `Code promo appliqué: ${code} (-${discount}€)`,
    metadata: { code, discount }
  });
}

/**
 * Log coupon retiré
 */
export async function logCouponRemoved(reservationId, userId, userName, { code }) {
  return logActivity({
    type: ACTIVITY_TYPES.COUPON_REMOVED,
    reservationId,
    userId,
    userName,
    description: `Code promo retiré: ${code}`,
    metadata: { code }
  });
}